import { Extension } from "@tiptap/core";

export type TextAlignValue = "left" | "right" | "center" | "justify";

export interface TextAlignOptions {
  /** Node types this extension targets — typically paragraph and heading. */
  types: string[];
  alignments: TextAlignValue[];
  defaultAlignment: TextAlignValue;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    textAlign: {
      setTextAlign: (alignment: TextAlignValue) => ReturnType;
      unsetTextAlign: () => ReturnType;
    };
  }
}

/**
 * Per-block text alignment. Adds a `textAlign` attribute to the configured
 * node types (default: paragraph and heading) and renders it as inline CSS.
 *
 * LaTeX export wraps the block in \begin{center} / \begin{flushleft} /
 * \begin{flushright} on the API side; for `justify` (the default in LaTeX)
 * we omit the attribute to keep the source clean.
 */
export const TextAlign = Extension.create<TextAlignOptions>({
  name: "textAlign",

  addOptions() {
    return {
      types: ["paragraph", "heading"],
      alignments: ["left", "right", "center", "justify"],
      defaultAlignment: "left",
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textAlign: {
            default: this.options.defaultAlignment,
            parseHTML: (element) => {
              const value = element.style.textAlign || this.options.defaultAlignment;
              return this.options.alignments.includes(value as TextAlignValue)
                ? value
                : this.options.defaultAlignment;
            },
            renderHTML: (attributes) => {
              const align = attributes.textAlign;
              if (!align || align === this.options.defaultAlignment) return {};
              return { style: `text-align: ${align}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setTextAlign:
        (alignment) =>
        ({ commands }) => {
          if (!this.options.alignments.includes(alignment)) return false;
          return this.options.types
            .map((type) => commands.updateAttributes(type, { textAlign: alignment }))
            .every(Boolean);
        },
      unsetTextAlign:
        () =>
        ({ commands }) => {
          return this.options.types
            .map((type) => commands.resetAttributes(type, "textAlign"))
            .every(Boolean);
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      "Mod-Shift-l": () => this.editor.commands.setTextAlign("left"),
      "Mod-Shift-e": () => this.editor.commands.setTextAlign("center"),
      "Mod-Shift-r": () => this.editor.commands.setTextAlign("right"),
      "Mod-Shift-j": () => this.editor.commands.setTextAlign("justify"),
    };
  },
});
